import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { playZap } from "../utils/sound";
import { isLowPower } from "../utils/perf";

// One click "tear": a shockwave ring plus three chromatic blades ripping outward.
const Tear = ({ x, y }) => (
  <div aria-hidden="true" className="pointer-events-none fixed z-[97]" style={{ left: x, top: y }}>
    <motion.span
      className="absolute block rounded-full border-2 border-volt"
      style={{ width: 22, height: 22, x: "-50%", y: "-50%" }}
      initial={{ scale: 0.2, opacity: 0.9 }}
      animate={{ scale: 13, opacity: 0 }}
      transition={{ duration: 0.85, ease: "easeOut" }}
    />
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="absolute block h-[2px] w-24 sm:w-32 origin-left"
        style={{
          rotate: i * 60 - 90,
          background: i === 1 ? "rgba(79,179,191,0.85)" : i === 2 ? "rgba(124,131,219,0.85)" : "rgba(233,180,76,0.85)",
        }}
        initial={{ scaleX: 0, opacity: 0.95 }}
        animate={{ scaleX: [0, 1, 1], opacity: [0.95, 0.9, 0] }}
        transition={{ duration: 0.65, times: [0, 0.22, 1], ease: "easeOut" }}
      />
    ))}
  </div>
);

// Classic pixel "pointer hand" drawn from a bitmap — replaces the ring cursor.
const HAND_MAP = [
  "....XX........",
  "...X..X.......",
  "...X..X.......",
  "...X..X.......",
  "...X..X..XXX..",
  "...X..XXX..X..",
  "X..X..XX....X.",
  "XX.X..X......X",
  "X..X.........X",
  "X...........X.",
  "X...........X.",
  ".X.........X..",
  "..X.......X...",
  "...X.....X....",
  "....XXXXX.....",
];
const CELL = 2;
const handCells = [];
HAND_MAP.forEach((row, y) => {
  row.split("").forEach((c, x) => {
    if (c === "X") handCells.push([x, y]);
  });
});
const isFill = (x, y) => HAND_MAP[y] && HAND_MAP[y][x] === "X";
const isOutline = (x, y) => {
  if (isFill(x, y)) return false;
  for (let dy = -1; dy <= 1; dy++)
    for (let dx = -1; dx <= 1; dx++) if (isFill(x + dx, y + dy)) return true;
  return false;
};
const outlineCells = [];
HAND_MAP.forEach((row, y) => {
  for (let x = -1; x <= row.length; x++) if (isOutline(x, y)) outlineCells.push([x, y]);
});

const PixelHand = ({ hovering }) => (
  <svg
    width={HAND_MAP[0].length * CELL}
    height={HAND_MAP.length * CELL}
    viewBox={`${-CELL} 0 ${(HAND_MAP[0].length + 1) * CELL} ${HAND_MAP.length * CELL}`}
    style={{
      filter: hovering
        ? "drop-shadow(0 0 8px rgba(233,180,76,0.9))"
        : "drop-shadow(0 2px 6px rgba(0,0,0,0.6))",
    }}
    aria-hidden="true"
  >
    {outlineCells.map(([x, y], i) => (
      <rect key={`o${i}`} x={x * CELL} y={y * CELL} width={CELL} height={CELL} fill="#0B111B" />
    ))}
    {handCells.map(([x, y], i) => (
      <rect
        key={`f${i}`}
        x={x * CELL}
        y={y * CELL}
        width={CELL}
        height={CELL}
        fill={hovering ? "#E9B44C" : "#EDF1F7"}
      />
    ))}
  </svg>
);

export const UnseenCursor = () => {
  const reduce = useReducedMotion();
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [tears, setTears] = useState([]);

  const x = useMotionValue(-200);
  const y = useMotionValue(-200);
  const ringX = useSpring(x, { stiffness: 420, damping: 28, mass: 0.5 });
  const ringY = useSpring(y, { stiffness: 150, damping: 15, mass: 0.5 });
  const blobX = useSpring(x, { stiffness: 55, damping: 16, mass: 0.8 });
  const blobY = useSpring(y, { stiffness: 55, damping: 16, mass: 0.8 });

  useEffect(() => {
    if (reduce || !window.matchMedia("(pointer: fine)").matches) return undefined;
    setEnabled(true);
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    const over = (e) =>
      setHovering(
        !!e.target.closest("a, button, [role='button'], input, select, textarea, [data-cursor='magnetic']")
      );
    const down = (e) => {
      if (e.target.closest("input, textarea, select")) return;
      playZap();
      const id = `${Date.now()}-${Math.random()}`;
      setTears((t) => [...t.slice(-4), { id, x: e.clientX, y: e.clientY }]);
      setTimeout(() => setTears((t) => t.filter((i) => i.id !== id)), 950);
    };
    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    window.addEventListener("pointerdown", down, { passive: true });
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      window.removeEventListener("pointerdown", down);
    };
  }, [reduce, x, y]);

  if (!enabled) return null;

  const low = isLowPower();

  return (
    <>
      {!low && (
        <motion.div
          aria-hidden="true"
          className="pointer-events-none fixed left-0 top-0 z-[96] h-28 w-28 rounded-full"
          style={{
            x: blobX,
            y: blobY,
            translateX: "-50%",
            translateY: "-50%",
            background: "radial-gradient(circle, rgba(79,179,191,0.13), transparent 68%)",
          }}
        />
      )}
      {/* pixel hand cursor — springs behind the pointer, tilts on interactive hover */}
      <motion.div
        aria-hidden="true"
        data-testid="custom-cursor-ring"
        className="pointer-events-none fixed left-0 top-0 z-[99]"
        style={{ x: ringX, y: ringY }}
      >
        <motion.div
          animate={{ rotate: hovering ? -14 : 0, scale: hovering ? 1.18 : 1 }}
          transition={{ type: "spring", stiffness: 340, damping: 16 }}
          style={{ translateX: "-9px", translateY: "-2px" }}
        >
          <PixelHand hovering={hovering} />
        </motion.div>
      </motion.div>
      <AnimatePresence>
        {tears.map((t) => (
          <Tear key={t.id} x={t.x} y={t.y} />
        ))}
      </AnimatePresence>
    </>
  );
};
